import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './OptionScope.css';

function fmtIdx(n) {
  return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPnl(n) {
  const v = Number(n) || 0;
  const sign = v > 0 ? '+' : '';
  return `${sign}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtStrike(n) {
  return Number(n || 0).toLocaleString('en-IN');
}

function fmtOiLabel(n) {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(Math.round(n || 0));
}

function IndexTicker({ card, active, onSelect, arming }) {
  if (!card) return null;
  const short = (card.label || card.id || '')
    .replace(/^NIFTY\s+/i, '')
    .replace(/\s+NIFTY$/i, '')
    .replace('INDIA ', '');
  return (
    <button
      type="button"
      className={`os-ticker ${active ? 'active' : ''} ${card.up ? 'up' : 'down'} ${!card.tradeable ? 'watch' : ''}`}
      onClick={() => onSelect?.(card)}
      disabled={arming && active}
      title={card.tradeable ? `Chart + auto-trade ${card.id}` : `${card.id} chart only`}
    >
      <span className="os-ticker-name">{short || card.id}</span>
      <span className="os-ticker-price">{fmtIdx(card.price)}</span>
      <span className="os-ticker-chg">
        {card.changePts >= 0 ? '+' : ''}
        {fmtIdx(card.changePts)}
        {' '}
        ({card.changePct >= 0 ? '+' : ''}
        {fmtIdx(card.changePct)}%)
      </span>
      {!card.tradeable ? <em className="os-ticker-tag">CHART</em> : null}
    </button>
  );
}

function OiBar({ value, max, tone }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="os-oi-cell">
      <span>{fmtOiLabel(value)}</span>
      <div className="os-oi-track">
        <div className={`os-oi-fill ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PcrGauge({ pcr }) {
  const pct = Math.max(4, Math.min(96, ((Number(pcr) || 1) - 0.55) / 1.1 * 100));
  return (
    <div className="os-card os-pcr">
      <h3>Put/Call Ratio</h3>
      <div className="os-pcr-value">{Number(pcr || 0).toFixed(2)}</div>
      <div className="os-pcr-gauge">
        <div className="os-pcr-gradient" />
        <div className="os-pcr-marker" style={{ left: `${pct}%` }} />
      </div>
      <div className="os-pcr-labels">
        <span>Bearish</span>
        <span>Neutral</span>
        <span>Bullish</span>
      </div>
    </div>
  );
}

function AutoSideCard({ card, stopLossRs, marginRs, recheckLeft, pollSeconds }) {
  if (!card) return null;
  const tone = card.side === 'CE' ? 'call' : 'put';
  const stop = card.stopLossRs ?? stopLossRs ?? 0;
  const margin = card.marginRs ?? marginRs ?? 0;
  const poll = card.pollSeconds ?? pollSeconds ?? 120;
  const mm = Math.floor(Math.max(0, recheckLeft) / 60);
  const ss = String(Math.max(0, recheckLeft) % 60).padStart(2, '0');
  return (
    <div className={`os-auto-side ${tone} ${card.active ? 'active' : ''} ${card.ready ? 'ready' : ''}`}>
      <div className="os-auto-side-top">
        <strong>{card.label}</strong>
        <em>{card.state}</em>
      </div>
      <div className="os-auto-side-body">
        <span>Strike {fmtStrike(card.strike)}</span>
        <span>LTP {card.ltp != null ? card.ltp : '—'}</span>
      </div>
      <div className="os-auto-side-meta">
        <span>Stop {stop > 0 ? `₹${stop}` : '0% hold'}</span>
        <span>Margin ₹{Number(margin).toLocaleString('en-IN')}</span>
      </div>
      <div className="os-auto-side-timer">
        Recheck every {Math.round(poll / 60)} min · next {mm}:{ss}
      </div>
    </div>
  );
}

function ScopeSettings({ settings, onSaved }) {
  const [form, setForm] = useState({
    stopLossPerTrade: 200,
    maxDailyInvestment: 30000,
    pollSeconds: 120,
    dailyTarget: 10000
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!settings) return;
    setForm({
      stopLossPerTrade: Number(settings.stopLossPerTrade) || 200,
      maxDailyInvestment: Number(settings.maxDailyInvestment) || 30000,
      pollSeconds: Number(settings.pollSeconds) || 120,
      dailyTarget: Number(settings.dailyTarget) || 10000
    });
  }, [settings]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const body = {
        stopLossPerTrade: Math.max(0, Number(form.stopLossPerTrade) || 0),
        maxDailyInvestment: Math.max(50, Number(form.maxDailyInvestment) || 30000),
        pollSeconds: Math.max(60, Math.min(300, Number(form.pollSeconds) || 120)),
        dailyTarget: Math.max(50, Number(form.dailyTarget) || 10000),
        takeProfitPerTrade: 500,
        breakevenLockAt: 250,
        earlyStopPerTrade: Math.max(0, Math.round((Number(form.stopLossPerTrade) || 0) * 0.4))
      };
      const { data } = await axios.post('/api/automation/settings', body, { timeout: 12000 });
      setMsg('Saved');
      onSaved?.(data?.settings || body);
    } catch (err) {
      setMsg(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="os-settings" onSubmit={save}>
      <header className="os-settings-head">
        <h3>Trade controls</h3>
        <span>Stop · margin · day goal · recheck</span>
      </header>
      <div className="os-settings-grid">
        <label>
          <span>Stop loss (₹)</span>
          <input
            type="number"
            min="0"
            max="50000"
            step="5"
            value={form.stopLossPerTrade}
            onChange={(e) => setForm((f) => ({ ...f, stopLossPerTrade: Number(e.target.value) }))}
          />
        </label>
        <label>
          <span>Day margin (₹)</span>
          <input
            type="number"
            min="50"
            max="500000"
            step="50"
            value={form.maxDailyInvestment}
            onChange={(e) => setForm((f) => ({ ...f, maxDailyInvestment: Number(e.target.value) }))}
          />
        </label>
        <label>
          <span>Recheck (min)</span>
          <input
            type="number"
            min="1"
            max="5"
            step="1"
            value={Math.round((Number(form.pollSeconds) || 120) / 60)}
            onChange={(e) => setForm((f) => ({
              ...f,
              pollSeconds: Math.max(1, Math.min(5, Number(e.target.value) || 2)) * 60
            }))}
          />
        </label>
        <label>
          <span>Day goal (₹)</span>
          <input
            type="number"
            min="50"
            max="100000"
            step="50"
            value={form.dailyTarget}
            onChange={(e) => setForm((f) => ({ ...f, dailyTarget: Number(e.target.value) }))}
          />
        </label>
      </div>
      <div className="os-settings-actions">
        <button type="submit" className="os-btn" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {msg ? <span className="os-settings-msg">{msg}</span> : null}
      </div>
    </form>
  );
}

function ProfitBoard({ profit }) {
  if (!profit) {
    return (
      <section className="os-profit-board">
        <div className="os-profit-empty">Loading profit board…</div>
      </section>
    );
  }
  const dayNet = Number(profit.dayNet) || 0;
  const realized = Number(profit.realizedPnl) || 0;
  const unrealized = Number(profit.unrealizedPnl) || 0;
  const target = Number(profit.dayTarget) || 10000;
  const pct = Math.min(100, Math.max(0, Number(profit.progressPct) || 0));
  const open = profit.open;
  const scenario = profit.scenario;
  const tone = dayNet > 0.004 ? 'earn' : dayNet < -0.004 ? 'loss' : 'flat';

  return (
    <section className={`os-profit-board ${tone}`}>
      <header className="os-profit-head">
        <div>
          <h2>Today&apos;s profit · Real-world</h2>
          <p>{profit.state || '—'}</p>
          {scenario?.name ? (
            <p className="os-scenario-name">
              Scenario: <strong>{scenario.name}</strong>
              {scenario.quality != null ? ` · quality ${scenario.quality}` : ''}
              {scenario.allowEntry ? ' · READY' : ' · WAIT'}
            </p>
          ) : null}
        </div>
        <div className={`os-profit-hero ${tone}`}>
          <span className="os-profit-hero-label">Day net</span>
          <strong>{fmtPnl(dayNet)}</strong>
        </div>
      </header>

      <div className="os-profit-metrics">
        <div>
          <span>Booked (closed)</span>
          <strong className={realized >= 0 ? 'up' : 'down'}>{fmtPnl(realized)}</strong>
        </div>
        <div>
          <span>Open trade</span>
          <strong className={unrealized >= 0 ? 'up' : 'down'}>{fmtPnl(unrealized)}</strong>
        </div>
        <div>
          <span>Bank from</span>
          <strong>₹{Number(profit.bankProfitRs || 0).toLocaleString('en-IN')}</strong>
        </div>
        <div>
          <span>Trade target</span>
          <strong>₹{Number(profit.takeProfitRs || 0).toLocaleString('en-IN')}</strong>
        </div>
      </div>

      <div className="os-profit-progress" aria-label="Day goal progress">
        <div className="os-profit-progress-bar" style={{ width: `${pct}%` }} />
        <em>{pct.toFixed(0)}% of day goal ₹{target.toLocaleString('en-IN')}</em>
      </div>

      {scenario?.playbook?.length ? (
        <ol className="os-playbook">
          {scenario.playbook.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      ) : null}

      {scenario?.reasons?.length ? (
        <p className="os-analysis-reasons">{scenario.reasons.slice(0, 4).join(' · ')}</p>
      ) : null}

      {open ? (
        <div className={`os-open-trade ${open.unrealized >= 0 ? 'up' : 'down'}`}>
          <div className="os-open-trade-top">
            <strong>
              Open 1 lot {open.side} {fmtStrike(open.strike)}
            </strong>
            <span className={open.unrealized >= 0 ? 'up' : 'down'}>{fmtPnl(open.unrealized)}</span>
          </div>
          <div className="os-open-trade-grid">
            <span>Entry prem <b>{fmtIdx(open.entryPremium)}</b></span>
            <span>Live prem <b>{fmtIdx(open.livePremium)}</b></span>
            <span>Trade target <b>₹{Number(open.tradeTargetRs).toLocaleString('en-IN')}</b></span>
            <span>
              {open.hitTradeTarget
                ? 'In target zone'
                : `Need ${fmtPnl(open.remainingToTradeTarget)} more`}
            </span>
          </div>
          <p className="os-open-trade-plan">{open.strategy}</p>
          {open.targetSpot != null && (
            <p className="os-open-trade-map">
              Map: Entry {fmtIdx(open.entrySpot)}
              {' → '}
              Target {fmtIdx(open.targetSpot)}
              {' · '}
              Stop {fmtIdx(open.stopSpot)}
            </p>
          )}
        </div>
      ) : (
        <p className="os-profit-wait">
          No open lot — bot waits for real VWAP+EMA confluence, then buys 1 lot and shows live profit here.
        </p>
      )}

      {(profit.lines || []).length > 0 && (
        <ul className="os-profit-lines">
          {profit.lines.map((line) => (
            <li key={line.id}>
              <span className={`os-side-badge ${line.side === 'CE' ? 'ce' : 'pe'}`}>{line.side}</span>
              <span>{line.label} · {line.status}</span>
              <strong className={(line.netPnl ?? line.pnl) >= 0 ? 'up' : 'down'}>
                {fmtPnl(line.netPnl ?? line.pnl)}
              </strong>
            </li>
          ))}
        </ul>
      )}

      <p className="os-profit-foot">{profit.summary}</p>
      {profit.paperNote ? <p className="os-profit-paper">{profit.paperNote}</p> : null}
    </section>
  );
}

function CenterChart({ chart }) {
  const rows = chart?.candles || [];
  const w = 1100;
  const h = 420;
  const padL = 58;
  const padR = 14;
  const padT = 16;
  const padB = 26;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;
  const title = chart?.label || chart?.symbol || 'Index';
  const analysis = chart?.analysis;
  const plan = chart?.plan;

  if (!rows.length) {
    return (
      <section className="os-center-chart">
        <header className="os-chart-head">
          <div>
            <h2>{title}</h2>
            <p>Intraday 5m · loading live feed</p>
          </div>
          <strong className="os-chart-spot">{fmtIdx(chart?.spot)}</strong>
        </header>
        <div className="os-chart-empty">Loading chart…</div>
      </section>
    );
  }

  const strategyVals = (chart.strategyLines || []).map((l) => Number(l.value)).filter((n) => n > 0);
  const lows = rows.map((c) => c.low);
  const highs = rows.map((c) => c.high);
  const levels = [
    chart.support, chart.resistance, chart.atm, chart.maxPain, chart.spot,
    chart.vwap, ...strategyVals
  ].filter((n) => Number(n) > 0);
  const min = Math.min(...lows, ...levels);
  const max = Math.max(...highs, ...levels);
  const range = max - min || 1;
  const y = (v) => padT + (1 - (v - min) / range) * plotH;
  const barW = plotW / rows.length;
  const last = rows[rows.length - 1];
  const up = (chart.dayChangePts || 0) >= 0;
  const linePts = rows
    .map((c, i) => `${padL + i * barW + barW / 2},${y(c.close)}`)
    .join(' ');

  const levelLines = [
    { key: 'vwap', label: 'VWAP', value: chart.vwap, color: '#2962ff', dash: '6 3' },
    { key: 'atm', label: 'ATM', value: chart.atm, color: '#fbbf24', dash: '4 4' },
    { key: 'sup', label: 'S', value: chart.support, color: '#34d399', dash: '4 4' },
    { key: 'res', label: 'R', value: chart.resistance, color: '#f87171', dash: '4 4' },
    { key: 'pain', label: 'MP', value: chart.maxPain, color: '#94a3b8', dash: '4 4' },
    ...(chart.strategyLines || []).map((l) => ({
      key: l.key,
      label: l.label,
      value: l.value,
      color: l.color,
      dash: l.key === 'entry' ? undefined : '5 3',
      solid: l.key === 'entry'
    }))
  ].filter((l) => Number(l.value) > 0);

  const yTicks = 5;
  const tickVals = Array.from({ length: yTicks }, (_, i) => min + (range * i) / (yTicks - 1));
  const biasClass = analysis?.bias === 'CE' ? 'up' : analysis?.bias === 'PE' ? 'down' : '';

  return (
    <section className="os-center-chart">
      <header className="os-chart-head">
        <div>
          <h2>{title} · Intraday</h2>
          <p>
            5m · {rows.length} bars
            {analysis?.ok
              ? ` · lean ${analysis.bias} ${analysis.confidence}%`
              : (chart.tradeable === false ? ' · chart only' : ' · analysing…')}
            {plan ? ` · plan: ${plan.side} ${plan.strike}` : ''}
          </p>
        </div>
        <div className="os-chart-price">
          <strong className={last.bull ? 'up' : 'down'}>{fmtIdx(chart.spot || last.close)}</strong>
          <span className={up ? 'up' : 'down'}>
            {up ? '+' : ''}
            {fmtIdx(chart.dayChangePts)} ({up ? '+' : ''}
            {fmtIdx(chart.dayChangePct)}%)
          </span>
          {analysis?.ok && (
            <span className={`os-bias-pill ${biasClass}`}>
              {analysis.bias === 'neutral' ? 'NEUTRAL' : analysis.bias === 'CE' ? 'BUY CALL' : 'BUY PUT'}
              {' · '}
              {analysis.confidence}%
            </span>
          )}
        </div>
      </header>
      <svg viewBox={`0 0 ${w} ${h}`} className="os-big-chart-svg" role="img" aria-label={`${title} intraday chart`}>
        {tickVals.map((v) => (
          <g key={`yt-${v}`}>
            <line x1={padL} y1={y(v)} x2={w - padR} y2={y(v)} className="os-chart-grid" />
            <text x={padL - 8} y={y(v) + 4} className="os-chart-ylabel" textAnchor="end">
              {Math.round(v).toLocaleString('en-IN')}
            </text>
          </g>
        ))}
        {levelLines.map((l) => (
          <g key={l.key}>
            <line
              x1={padL}
              y1={y(l.value)}
              x2={w - padR}
              y2={y(l.value)}
              stroke={l.color}
              strokeWidth={l.solid ? 1.6 : 1.1}
              strokeDasharray={l.dash || (l.solid ? undefined : '4 4')}
              opacity="0.9"
            />
            <text x={w - padR - 2} y={y(l.value) - 4} fill={l.color} className="os-chart-level" textAnchor="end">
              {l.label} {Math.round(l.value).toLocaleString('en-IN')}
            </text>
          </g>
        ))}
        <polyline points={linePts} className="os-chart-line" fill="none" />
        {rows.map((c, i) => {
          const x = padL + i * barW + barW * 0.18;
          const bw = Math.max(2, barW * 0.64);
          const color = c.bull ? '#3fb950' : '#f85149';
          const yO = y(c.open);
          const yC = y(c.close);
          return (
            <g key={`c-${c.t || i}`}>
              <line x1={x + bw / 2} y1={y(c.high)} x2={x + bw / 2} y2={y(c.low)} stroke={color} strokeWidth="1.1" />
              <rect
                x={x}
                y={Math.min(yO, yC)}
                width={bw}
                height={Math.max(2, Math.abs(yO - yC))}
                fill={color}
                rx="0.5"
              />
            </g>
          );
        })}
      </svg>
      {(plan || analysis?.reasons?.length) && (
        <div className="os-strategy-map">
          {plan ? (
            <p>
              <strong>Strategy map:</strong>
              {' '}
              {plan.strategy}
              {' · '}
              Entry {fmtIdx(plan.entrySpot)}
              {' → '}
              Target {fmtIdx(plan.targetSpot)}
              {' · '}
              Stop {fmtIdx(plan.stopSpot)}
            </p>
          ) : null}
          {analysis?.reasons?.length ? (
            <p className="os-analysis-reasons">{analysis.reasons.join(' · ')}</p>
          ) : null}
        </div>
      )}
    </section>
  );
}

export default function OptionScope({ pollMs = 8000 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [recheckLeft, setRecheckLeft] = useState(120);
  const [selectedId, setSelectedId] = useState('NIFTY');
  const [arming, setArming] = useState(false);
  const [armNote, setArmNote] = useState('');

  const refresh = useCallback(async (indexId) => {
    const id = indexId || selectedId || 'NIFTY';
    try {
      const { data: payload } = await axios.get('/api/options/scope', {
        params: { index: id },
        timeout: 25000
      });
      if (payload?.ok === false) {
        setError(payload.error || 'OptionScope failed');
        return;
      }
      setData(payload);
      if (payload.selectedIndex) setSelectedId(payload.selectedIndex);
      setError(null);
      const poll = Number(payload?.auto?.pollSeconds || payload?.settings?.pollSeconds) || 120;
      setRecheckLeft(poll);
    } catch (e) {
      setError(e.message || 'Cannot load OptionScope');
    }
  }, [selectedId]);

  useEffect(() => {
    refresh(selectedId);
    const t = setInterval(() => refresh(selectedId), pollMs);
    return () => clearInterval(t);
  }, [selectedId, pollMs, refresh]);

  useEffect(() => {
    const t = setInterval(() => {
      setRecheckLeft((s) => (s <= 1
        ? (Number(data?.auto?.pollSeconds || data?.settings?.pollSeconds) || 120)
        : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [data?.auto?.pollSeconds, data?.settings?.pollSeconds]);

  const selectIndex = async (card) => {
    if (!card?.id || card.id === selectedId) return;
    setSelectedId(card.id);
    setArmNote('');
    if (!card.tradeable) {
      setArmNote(`${card.label} — chart only (no option auto-buy)`);
      return;
    }
    setArming(true);
    try {
      const lotSize = Number(card.lotSize) > 0
        ? Number(card.lotSize)
        : ({ NIFTY: 65, BANKNIFTY: 15, FINNIFTY: 25, MIDCPNIFTY: 50, SENSEX: 10 }[card.id] || 65);
      await axios.post('/api/automation/settings', {
        underlyingName: card.id,
        symbol: card.yahoo,
        strikeStep: card.strikeStep || 50,
        lots: 1,
        lotSize,
        enabled: true,
        handsFree: true
      }, { timeout: 12000 });
      await axios.post('/api/automation/enable', {}, { timeout: 8000 }).catch(() => null);
      setArmNote(`Intraday armed · ${card.id} · analyse → 1 lot Call/Put · strategy on chart`);
      await refresh(card.id);
    } catch (err) {
      setArmNote(err.response?.data?.message || err.message || 'Could not arm auto-buy');
    } finally {
      setArming(false);
    }
  };

  const chain = data?.chain;
  const levels = data?.keyLevels;
  const pnl = data?.todayPnl;
  const profit = data?.profitBoard || data?.profit;
  const auto = data?.auto;
  const chart = data?.chart;
  const tomorrow = data?.tomorrow;
  const indexList = data?.indexList
    || Object.values(data?.indices || {}).filter((x) => x && x.id);
  const stopRs = auto?.stopLossRs ?? data?.settings?.stopLossPerTrade ?? 0;
  const marginRs = auto?.marginRs ?? data?.settings?.maxDailyInvestment ?? 0;
  const pollSec = auto?.pollSeconds ?? data?.settings?.pollSeconds ?? 120;
  const zeroLoss = stopRs <= 0;

  return (
    <div className="os-app os-kite">
      <div className="os-ticker-strip" role="tablist" aria-label="Indexes">
        {indexList.map((card) => (
          <IndexTicker
            key={card.id}
            card={card}
            active={card.id === selectedId}
            onSelect={selectIndex}
            arming={arming}
          />
        ))}
        {!indexList.length && (
          <div className="os-ticker-loading">Loading indexes…</div>
        )}
      </div>

      <header className="os-header-slim">
        <div className="os-brand-slim">
          <strong>OptionScope · Intraday</strong>
          <span>{data?.subtitle || 'Analyse index → 1 lot Call/Put → profit on screen'}</span>
        </div>
        <div className="os-header-status">
          <span className="os-pill live">INTRADAY 1 LOT</span>
          <span className={`os-pill ${((profit?.dayNet) || 0) >= 0 ? 'zero' : 'status'}`}>
            DAY {fmtPnl(profit?.dayNet || 0)}
          </span>
          <span className="os-pill zero">{zeroLoss ? '0% LOSS' : `STOP ₹${stopRs}`}</span>
          <span className="os-pill status">{(auto?.status || 'idle').replace(/_/g, ' ')}</span>
        </div>
      </header>

      <div className="os-stage">
        <ProfitBoard profit={profit} />
        <CenterChart chart={chart} />

        <div className="os-auto-dock">
          <div className="os-auto-dock-head">
            <p className="os-auto-action">{auto?.lastAction || 'Select an index to chart · tradeable indexes auto-arm Call/Put'}</p>
            {(armNote || arming) && (
              <p className="os-arm-note">{arming ? 'Arming auto-buy…' : armNote}</p>
            )}
          </div>
          <div className="os-auto-sides">
            <AutoSideCard
              card={auto?.buyCall}
              stopLossRs={stopRs}
              marginRs={marginRs}
              recheckLeft={recheckLeft}
              pollSeconds={pollSec}
            />
            <AutoSideCard
              card={auto?.buyPut}
              stopLossRs={stopRs}
              marginRs={marginRs}
              recheckLeft={recheckLeft}
              pollSeconds={pollSec}
            />
          </div>
          {auto?.position && (
            <p className="os-auto-hold">
              Open {auto.position.side} {fmtStrike(auto.position.strike)}
              {' · '}
              <span className={auto.position.unrealized >= 0 ? 'up' : 'down'}>
                {fmtPnl(auto.position.unrealized)}
              </span>
              {' · '}
              {auto.position.holdNote}
            </p>
          )}
        </div>
      </div>

      {error && <div className="os-error">{error}</div>}

      {tomorrow?.ok && (
        <section className="os-card os-tomorrow">
          <header className="os-tomorrow-head">
            <div>
              <h3>{tomorrow.title}</h3>
              <p>{tomorrow.sessionLabel || tomorrow.tomorrowLabel}</p>
            </div>
            <a className="os-tomorrow-link" href="/Report">Report →</a>
          </header>
          <p className="os-tomorrow-summary">{tomorrow.plainSummary}</p>
        </section>
      )}

      <div className="os-grid">
        <section className="os-card os-chain">
          <header className="os-chain-head">
            <h2>
              {chain?.underlying || selectedId} Option Chain
              <span> · {chain?.expiryLabel || '—'}</span>
            </h2>
            <span className="os-chain-meta">updated {data?.updatedAt || '—'}</span>
          </header>
          <div className="os-table-wrap">
            <table className="os-table">
              <thead>
                <tr>
                  <th>OI</th>
                  <th>CHG OI</th>
                  <th>IV</th>
                  <th>LTP</th>
                  <th>Δ</th>
                  <th className="os-strike-col">STRIKE</th>
                  <th>Δ</th>
                  <th>LTP</th>
                  <th>IV</th>
                  <th>CHG OI</th>
                  <th>OI</th>
                </tr>
                <tr className="os-side-row">
                  <th colSpan={5} className="os-calls-label">CALLS</th>
                  <th />
                  <th colSpan={5} className="os-puts-label">PUTS</th>
                </tr>
              </thead>
              <tbody>
                {(chain?.rows || []).map((row) => (
                  <tr key={row.strike} className={row.atm ? 'atm' : ''}>
                    <td>
                      <OiBar value={row.call.oi} max={chain.maxCallOi} tone="call" />
                    </td>
                    <td className={row.call.chgOi >= 0 ? 'up' : 'down'}>{row.call.chgOiLabel}</td>
                    <td>{row.call.iv}</td>
                    <td className="os-ltp-call">{row.call.ltp}</td>
                    <td>{row.call.delta}</td>
                    <td className="os-strike-col">
                      {fmtStrike(row.strike)}
                      {row.atm ? <em className="os-atm-tag">ATM</em> : null}
                    </td>
                    <td>{row.put.delta}</td>
                    <td className="os-ltp-put">{row.put.ltp}</td>
                    <td>{row.put.iv}</td>
                    <td className={row.put.chgOi >= 0 ? 'up' : 'down'}>{row.put.chgOiLabel}</td>
                    <td>
                      <OiBar value={row.put.oi} max={chain.maxPutOi} tone="put" />
                    </td>
                  </tr>
                ))}
                {!chain?.rows?.length && (
                  <tr>
                    <td colSpan={11} className="os-empty">
                      {chart?.tradeable === false
                        ? 'India VIX has no option chain — select a tradeable index'
                        : 'Loading chain…'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="os-side">
          <ScopeSettings settings={data?.settings} onSaved={() => refresh(selectedId)} />
          <PcrGauge pcr={data?.pcr} />
          <div className="os-card os-levels">
            <h3>Key Levels</h3>
            <ul>
              <li>
                <span>Max Pain</span>
                <strong>{fmtStrike(levels?.maxPain)}</strong>
              </li>
              <li>
                <span>Support</span>
                <strong className="up">{fmtStrike(levels?.support)}</strong>
              </li>
              <li>
                <span>Resistance</span>
                <strong className="down">{fmtStrike(levels?.resistance)}</strong>
              </li>
              <li>
                <span>India VIX</span>
                <strong>{fmtIdx(levels?.vix)}</strong>
              </li>
            </ul>
          </div>
          <div className="os-card os-pnl">
            <h3>Today&apos;s P&amp;L</h3>
            <div className={`os-pnl-total ${((pnl?.totalNet ?? pnl?.total) || 0) >= 0 ? 'up' : 'down'}`}>
              {fmtPnl(pnl?.totalNet ?? pnl?.total)}
            </div>
            <p className="os-pnl-summary">{pnl?.summary || '0 open · 0 closed'}</p>
            <ul className="os-pnl-list">
              {(pnl?.lines || []).map((line) => (
                <li key={line.id}>
                  <span className={`os-side-badge ${line.side === 'CE' ? 'ce' : 'pe'}`}>{line.side}</span>
                  <span className="os-pnl-label">{line.label}</span>
                  <span className={(line.netPnl ?? line.pnl) >= 0 ? 'up' : 'down'}>
                    {fmtPnl(line.netPnl ?? line.pnl)}
                  </span>
                </li>
              ))}
              {!pnl?.lines?.length && (
                <li className="os-pnl-empty">No trades yet — pick an index above to auto-buy</li>
              )}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
